(function (root) {
  function numbers(locale, currency) {
    const money = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const digits = money.resolvedOptions().maximumFractionDigits;
    const number = new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits });
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const decimal = parts.find(part => part.type === 'decimal')?.value || '.';
    const group = parts.find(part => part.type === 'group')?.value || ',';
    const scale = 10 ** digits;
    return {
      digits, scale, money, number,
      minor(amount) {
        const parts = number.formatToParts(amount);
        return Number(parts.filter(part => part.type === 'integer' || part.type === 'fraction').map(part => part.value).join(''));
      },
      parse(text) {
        text = text.trim().replace(/[\u200e\u200f\u061c]/g, '');
        if (!text) return null;
        const groups = text.split(decimal);
        if (groups.length > 2 || (groups[1] !== undefined && (!/^\d+$/.test(groups[1]) || groups[1].length > digits))) return NaN;
        const whole = groups[0].replace(/[\u00a0\u202f]/g, ' ');
        const separator = group.replace(/[\u00a0\u202f]/g, ' ');
        const chunks = whole.split(separator);
        if (!/^\d+$/.test(chunks[0]) || (chunks.length > 1 && (chunks[0].length > 3 || chunks.slice(1).some(chunk => !/^\d{3}$/.test(chunk))))) return NaN;
        const value = Number(chunks.join('')) * scale + Number((groups[1] || '').padEnd(digits, '0'));
        return Number.isSafeInteger(value) ? value : NaN;
      },
    };
  }

  function gross(yen, taxMode) {
    return yen + (taxMode === 'add' || taxMode === 'catalog' ? Math.round(yen / 10) : 0);
  }

  function inverse(minor, side, rate, taxMode, format, nativeMaximum = 1e12) {
    if (minor === null) return '';
    const display = yen => format.minor(gross(yen, taxMode) * rate);
    let low = 0;
    let high = Math.min(Math.ceil((minor / format.scale + 1) / rate) + 2, Math.ceil(nativeMaximum) + 2, Number.MAX_SAFE_INTEGER - 1);
    while (low < high) {
      const middle = low + Math.floor((high - low) / 2);
      if (display(middle) < minor || (side === 'max' && display(middle) === minor)) low = middle + 1;
      else high = middle;
    }
    return String(side === 'max' ? low - 1 : low);
  }

  async function auditCatalog(fetchPage) {
    let taxable = false, exempt = false, count = 0, total, pages;
    for (let page = 1; pages === undefined || page <= pages; page++) {
      const data = await fetchPage(page);
      if (data?.v !== 1 || data.page !== page || !Number.isSafeInteger(data.pages) || data.pages < page ||
          !Number.isSafeInteger(data.total) || data.total < 0 || !Number.isSafeInteger(data.count) || data.count < 0 ||
          typeof data.taxable !== 'boolean' || typeof data.exempt !== 'boolean' || data.complete !== true ||
          (page > 1 && (data.total !== total || data.pages !== pages)) || (data.total > 0 && data.count === 0)) {
        throw new Error('Incomplete price-filter catalog');
      }
      total = data.total;
      pages = data.pages;
      count += data.count;
      taxable ||= data.taxable;
      exempt ||= data.exempt;
      if (taxable && exempt) return 'unsupported';
    }
    if (count !== total || (total > 0 && !taxable && !exempt)) throw new Error('Incomplete price-filter catalog');
    return taxable ? 'add' : 'none';
  }

  const math = { numbers, gross, inverse, auditCatalog };
  if (typeof module !== 'undefined' && module.exports) module.exports = math;
  if (!root.document) return;
  const configNode = document.getElementById('TTDisplayCurrencyConfig');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  let scheduled = false;
  const metadataKey = 'tt_price';
  const state = () => root.TTDisplayCurrency || { currency: 'JPY', rate: 1, ready: true };
  const format = () => numbers(config.locale || 'en', state().currency);
  const ranges = form => [...form.querySelectorAll('[data-tt-price-range]')];
  let catalogRequest, catalogMode;

  function resolveCatalog() {
    if (catalogRequest) return;
    catalogRequest = auditCatalog(async page => {
      const base = (config.root || '/').replace(/\/?$/, '/');
      const url = new URL(`${base}collections/all`, location.origin);
      url.searchParams.set('section_id', 'tt-price-filter-data');
      url.searchParams.set('page', page);
      const response = await root.fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Price-filter catalog unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = JSON.parse(html.querySelector('[data-tt-price-catalog]')?.textContent || 'null');
      if (data?.country !== config.country) throw new Error('Price-filter catalog country changed');
      return data;
    }).then(mode => { catalogMode = mode; }, () => { catalogMode = 'unsupported'; }).then(schedule);
  }

  function metadata(params, range) {
    try {
      const item = JSON.parse(params.get(metadataKey));
      const inputs = [...range.querySelectorAll('[data-tt-price-input]')];
      if (item?.v !== 2 || item.basis !== range.dataset.taxMode || item.country !== config.country || item.currency !== state().currency || item.rate !== state().rate) return null;
      if (!inputs.every(input => String(item.native[input.dataset.ttPriceInput]) === (params.get(input.dataset.nativeName) || ''))) return null;
      if (!['min', 'max'].every(key => item[key] === null || (Number.isSafeInteger(item[key]) && item[key] >= 0))) return null;
      return item;
    } catch { return null; }
  }

  function enhance(range) {
    if (range.dataset.enhanced) return;
    range.dataset.enhanced = 'true';
    range.querySelectorAll('[data-tt-price-input]').forEach(input => {
      input.type = 'text';
      input.dataset.nativeName = input.name;
      if (!input.hasAttribute('data-native-value')) input.dataset.nativeValue = input.value;
      input.removeAttribute('name');
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = input.dataset.nativeName;
      hidden.value = input.dataset.nativeValue;
      hidden.dataset.ttPriceNative = input.dataset.ttPriceInput;
      range.append(hidden);
      input.addEventListener('input', () => {
        range.querySelectorAll('[data-tt-price-input]').forEach(control => {
          control.setCustomValidity('');
          control.removeAttribute('aria-invalid');
        });
        const error = range.parentElement.querySelector('[data-tt-price-error]');
        if (error) error.hidden = true;
      });
    });
  }

  function error(range, message, report) {
    const input = range.querySelector('[data-tt-price-input]');
    input.setCustomValidity(message);
    input.setAttribute('aria-invalid', 'true');
    const output = range.parentElement.querySelector('[data-tt-price-error]');
    if (output) { output.hidden = false; output.textContent = message; }
    if (report) input.reportValidity();
    return false;
  }

  function prepare(form, report = true) {
    if (!form) return true;
    let item = null;
    for (const range of ranges(form)) {
      enhance(range);
      const f = format();
      const inputs = [...range.querySelectorAll('[data-tt-price-input]')];
      if (inputs.some(input => input.disabled)) {
        range.querySelectorAll('[data-tt-price-native]').forEach(input => { input.disabled = false; });
        continue;
      }
      const values = Object.fromEntries(inputs.map(input => [input.dataset.ttPriceInput, f.parse(input.value)]));
      if (Object.values(values).some(value => Number.isNaN(value))) return error(range, config.filter_invalid, report);
      if (values.min !== null && values.max !== null && values.min > values.max) return error(range, config.filter_reversed, report);
      const changed = inputs.some(input => input.value !== input.dataset.renderedValue);
      const native = {};
      for (const input of inputs) {
        const side = input.dataset.ttPriceInput;
        const hidden = range.querySelector(`[data-tt-price-native="${side}"]`);
        hidden.disabled = false;
        native[side] = changed ? inverse(values[side], side, state().rate, range.dataset.taxMode, f, Number(range.dataset.nativeMax)) : input.dataset.nativeValue;
      }
      if (native.max === '-1' || (native.min && native.max && Number(native.min) > Number(native.max))) {
        native.min = String(Math.ceil(Number(range.dataset.nativeMax)) + 1);
        native.max = native.min;
      }
      inputs.forEach(input => { range.querySelector(`[data-tt-price-native="${input.dataset.ttPriceInput}"]`).value = native[input.dataset.ttPriceInput]; });
      if (values.min !== null || values.max !== null) item = { v: 2, basis: range.dataset.taxMode, country: config.country, currency: state().currency, rate: state().rate, ...values, native };
    }
    let hidden = form.querySelector(`[name="${metadataKey}"]`);
    if (item && !hidden) { hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = metadataKey; form.append(hidden); }
    if (hidden) { hidden.disabled = !item; hidden.value = item ? JSON.stringify(item) : ''; }
    return true;
  }

  function render() {
    scheduled = false;
    const current = state();
    const f = format();
    const params = new URLSearchParams(location.search);
    document.querySelectorAll('[data-tt-price-range]').forEach(range => {
      enhance(range);
      if (range.dataset.taxMode === 'pending') {
        if (catalogMode) range.dataset.taxMode = catalogMode;
        else resolveCatalog();
      }
      const pending = range.dataset.taxMode === 'pending';
      const key = `${current.currency}/${current.rate}/${current.ready}/${range.dataset.taxMode}`;
      if (range.dataset.context === key) return;
      range.dataset.context = key;
      const item = metadata(params, range);
      const unsupported = range.dataset.taxMode === 'unsupported';
      range.querySelectorAll('[data-tt-price-input]').forEach(input => {
        const native = input.dataset.nativeValue;
        const minor = item ? item[input.dataset.ttPriceInput] : (native === '' ? null : f.minor(gross(Number(native), range.dataset.taxMode) * current.rate));
        input.value = minor === null ? '' : f.number.format(minor / f.scale);
        input.dataset.renderedValue = input.value;
        input.disabled = !current.ready || unsupported || pending;
        input.setAttribute('aria-describedby', `${range.querySelector('[data-tt-price-input]').id}-help`);
        if (input.dataset.ttPriceInput === 'max') input.placeholder = f.number.format(gross(Number(range.dataset.nativeMax), range.dataset.taxMode) * current.rate);
      });
      range.querySelectorAll('[data-tt-price-code]').forEach(element => { element.textContent = current.currency; });
      const help = range.parentElement.querySelector('[data-tt-price-help]');
      if (help) {
        help.id = `${range.querySelector('[data-tt-price-input]').id}-help`;
        let text = config.filter_notice.replaceAll('[currency]', current.currency).replaceAll('[example]', f.number.format(1234));
        if (current.ready && current.currency === 'JPY' && config.currencies?.[config.country] !== 'JPY') text = `${config.unavailable} ${text}`;
        help.textContent = pending ? range.dataset.loading : unsupported ? config.filter_unsupported : text;
      }
    });
    document.querySelectorAll('[data-tt-price-amount]').forEach(element => {
      const parent = document.querySelector('[data-tt-price-range]');
      const mode = parent?.dataset.taxMode || config.filter_tax_mode;
      if (mode === 'pending') {
        const loading = parent?.dataset.loading || '';
        if (element.textContent !== loading) element.textContent = loading;
        return;
      }
      const amount = gross(Number(element.dataset.ttPriceAmount) / 100, mode) * current.rate;
      const item = parent && metadata(params, parent);
      const bound = element.dataset.ttPriceBound;
      const value = item && ['min', 'max'].includes(bound) && item[bound] !== null ? item[bound] / f.scale : amount;
      const text = `${current.currency} ${f.number.format(value)}`;
      if (element.textContent !== text) element.textContent = text;
    });
    document.querySelectorAll('a[href]').forEach(link => {
      if (!params.has(metadataKey)) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      const same = ['filter.v.price.gte', 'filter.v.price.lte'].every(key => (url.searchParams.get(key) || '') === (params.get(key) || ''));
      if (same && [...url.searchParams.keys()].some(key => key.startsWith('filter.v.price.'))) url.searchParams.set(metadataKey, params.get(metadataKey));
      else url.searchParams.delete(metadataKey);
      if (link.href !== url.href) link.href = url.href;
    });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(render);
  }
  root.TTPriceFilter = { prepare, isEnhanced: element => Boolean(element.querySelector('[data-tt-price-range]')) };
  document.addEventListener('submit', event => {
    if (ranges(event.target).length && !prepare(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('tt:currency', schedule);
  window.addEventListener('popstate', schedule);
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  render();
})(typeof window === 'undefined' ? globalThis : window);
